import dayjs from "dayjs";
import { addDateTz } from "pages/media/utils";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import UTC from "dayjs/plugin/utc";
import {
  ContactResponse,
  FaceCategory,
  VoiceCategory,
  VoiceProfileResponse,
} from "pages/media/types";
import { LanguageOption } from "hooks/useSystemConfiguration/";
import { useExistingContact } from "../hooks/useExistingContact";
import { ActiveTabName, PremadeVoiceLanguageFilterEntry } from "../types";

export const timestampPresenter = (
  name: string,
  date: string,
  { utc, locale }: { utc: boolean; locale: string } = {
    utc: true,
    locale: "en",
  }
): string => {
  dayjs.extend(LocalizedFormat);
  dayjs.extend(UTC);
  let day = dayjs(addDateTz(date)).locale(locale);
  if (utc) {
    day = day.utc();
  }
  const formattedTime = day.format("L LT");
  return `${name} ${formattedTime}`;
};

export const categories = {
  Premade: "Premade",
  Professional: "Premade",
  Custom: "Custom",
  Cloned: "Custom",
};

export const isCustomCategory = (
  category: VoiceCategory | FaceCategory | undefined
) => {
  return category ? categories[category] === "Custom" : false;
};

// get full name of the script language
export const getLanguageName = (
  scriptLanguage: string,
  supportedLanguagesOptions: LanguageOption[]
) => {
  if (scriptLanguage === "") return;
  const language = supportedLanguagesOptions.find(
    (lang) =>
      lang.value === scriptLanguage || lang.value.startsWith(scriptLanguage)
  );
  return language?.label ?? scriptLanguage;
};

export const getInitialTab = (
  type: "voice" | "face",
  contact: ReturnType<typeof useExistingContact>["data"] | undefined
) => {
  const category =
    type === "face"
      ? contact?.presenter?.data.face_profile?.category
      : contact?.presenter?.data.voice_profile.category;
  if (!category && contact?.presenter?.data) return ActiveTabName.Premade;
  const isCustom = isCustomCategory(category);
  return isCustom ? ActiveTabName.Custom : ActiveTabName.Premade;
};

export const handleScroll =
  (isLoading: boolean, loadMoreData: () => void, hasMore: boolean) =>
  (event: Event) => {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as HTMLElement;

    // Load more data when we're 50% of the visible area from the bottom
    if (
      scrollHeight - scrollTop <= clientHeight * 1.5 &&
      hasMore &&
      !isLoading
    ) {
      loadMoreData();
    }
  };

export function getContactName(contact?: ContactResponse) {
  if (!contact) return "";
  const firstname = (contact.firstname ?? "").trim();
  const lastname = (contact.lastname ?? "").trim();
  const name = `${firstname} ${lastname}`.trim();
  return name || "N/A";
}

export function getProfileNameWithCategory(name?: string, category?: string) {
  let result = "";
  if (name) {
    result = name.split(" - ")[0]?.trim() ?? "";
    if (category) {
      result += " (" + category + ")";
    }
  }
  return result;
}

// Index signature makes this not a "weak type", so Partial<T> for any T is
// accepted — we only care about these three optional fields if present.
type FaceImageFields = {
  thumbnail_asset_path?: string | null;
  extracted_asset_path?: string | null;
  custom_asset_path?: string | null;
  [key: string]: unknown;
};

export function getFaceProfileImagePath(faceProfile?: FaceImageFields | null) {
  if (!faceProfile) return undefined;
  return (
    faceProfile.thumbnail_asset_path ??
    faceProfile.extracted_asset_path ??
    faceProfile.custom_asset_path
  );
}

/**
 * Returns the preview audio URL for the requested language, falling back to
 * the voice's original language when no language is requested. Returns null
 * when there is no preview (either the requested language is not supported
 * by the voice or the preview has not been generated yet).
 */
export function getVoicePreviewForLanguage(
  voice:
    | Pick<VoiceProfileResponse, "original_language" | "additional_languages">
    | null
    | undefined,
  langCode?: string
): string | null {
  if (!voice) return null;
  const original = voice.original_language;
  if (!langCode) {
    return original?.preview_asset_path ?? null;
  }
  if (original?.language === langCode) {
    return original.preview_asset_path ?? null;
  }
  const match = voice.additional_languages?.find(
    (l) => l.language === langCode
  );
  return match?.preview_asset_path ?? null;
}

/** Distinct language codes from the premade voice filters API (no accents). */
export function getVoiceLanguageCodes(
  entries: PremadeVoiceLanguageFilterEntry[]
): string[] {
  const seen = new Set<string>();
  for (const entry of entries) {
    seen.add(entry.language);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** Distinct accent labels for a given language code (from API variants). */
export function getVoiceAccentOptionsForLanguage(
  entries: PremadeVoiceLanguageFilterEntry[],
  language: string
): string[] {
  const seen = new Set<string>();
  for (const entry of entries) {
    if (entry.language !== language) continue;
    for (const variant of entry.variants ?? []) {
      const accent = variant?.accent;
      if (accent && typeof accent === "string") seen.add(accent);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** Distinct locale codes for a given language code (from API variants). */
export function getVoiceLocaleOptionsForLanguage(
  entries: PremadeVoiceLanguageFilterEntry[],
  language: string
): string[] {
  const seen = new Set<string>();
  for (const entry of entries) {
    if (entry.language !== language) continue;
    for (const variant of entry.variants ?? []) {
      const locale = variant?.locale;
      if (locale && typeof locale === "string") seen.add(locale);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

export function getProfileIdField(
  item: object | null | undefined
): "face_profile_id" | "voice_profile_id" | "" {
  if (!item) return "";
  if ("face_profile_id" in item) {
    return "face_profile_id";
  }
  if ("voice_profile_id" in item) {
    return "voice_profile_id";
  }
  return "";
}
